import axios from 'axios'
import cookies from 'vue-js-cookie'
import router from '@/router/index'
import i18n from '@/languages'
import AppHelper from '../helper'

// TODO: verify if can be replace by Vue Plugin
export default class Api {
  constructor (apiURL) {
    this.apiURL = apiURL
    this.initAPIConnector()
  }

  getURL () {
    return this.apiURL
  }

  isWithCredentials () {
    return true
  }

  initAPIConnector () {
    var instance = this

    this.apiConnector = axios.create({
      baseURL: instance.apiURL,
      method: 'post',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: instance.isWithCredentials(),
      // Prepares request data as form data
      transformRequest: [
        (data, headers) => {
          var formData = new FormData()
          for (var key in data) {
            var value = data[key]
            if (typeof value === 'object') {
              let shouldStringify = true

              for (let key in value) {
                if (value[key] && !AppHelper.empty(value[key].name)) {
                  shouldStringify = false
                  break
                }
              }

              if (shouldStringify) {
                value = JSON.stringify(value)
              }
            }
            formData.set(key, value)
          }

          return formData
        }
      ],
      // Convert response data to json
      transformResponse: [
        (data) => {
          try {
            if (!(data instanceof ArrayBuffer)) {
              data = JSON.parse(data)
            }
          } catch (err) {
            if (process.env.NODE_ENV != 'production') {
              console.log(err)
            }
          }

          return data
        }
      ]
    })

    this.apiConnector.interceptors.response.use(function (response) {
      if (response.data.success) {
        if (!AppHelper.empty(response.data.result)) {
          var result = response.data.result
          if (!AppHelper.empty(result.accessKey)) {
            cookies.remove('accessKey')
            cookies.set('accessKey', result.accessKey)
          }
          if (!AppHelper.empty(result.crmid)) {
            cookies.remove('crmid')
            cookies.set('crmid', result.crmid)
          }
        }
      } else if (!AppHelper.empty(response.data.error)) {
        // If authentification failed redirects to login page
        response.data.error['label'] = instance.getErrorLabel(response.data.error.code)

        if (response.data.error.code == 604) {
          instance.clearCookies()
          router.push({ name: 'Login' })
        } else if (response.data.error.code == 601 || response.data.error.code == 602) {
          AppHelper.notify(response.data.error.label, 'error')
        }
      }

      return response.data
    })

    this.apiConnector.interceptors.request.use(function (request) {
      // Appends common request data for api operations
      if (!request.ignoreDefaultParams) {
        let commonRequestData = instance.getCommonRequestData()
        for (let key in commonRequestData) {
          request.data[key] = commonRequestData[key]
        }
      }

      request.data['height'] = window.screen.availHeight
      request.data['width'] = window.screen.availWidth

      return request
    })
  }

  getCommonRequestData () {
    return {
      'key': cookies.get('accessKey'),
      'gdpr_record_crmid': cookies.get('crmid')
    }
  }

  getErrorLabel (code) {
    switch (code) {
      case 601:
      case 602:
      default:
        return i18n.t('LBL_SYSTEM_ERROR')
        break
      case 603:
        return i18n.t('LBL_PORTAL_ACCESS_REFUSED')
        break
      case 604:
      case 605:
        return i18n.t('LBL_LOGIN_ERROR')
        break
      case 606:
        return i18n.t('LBL_EMAIL_IS_NOT_REGISTERED')
        break
      case 607:
      case 608:
        return i18n.t('LBL_CREDENTIALS_ALREADY_EXIST')
        break
    }
  }

  static getInstance () {
    if (this.instance == null) {
      this.instance = new Api()
    }

    return this.instance
  }

  clearCookies () {
    cookies.remove('accessKey')
    cookies.remove('crmid')
  }

  async logout () {
    this.clearCookies()
    return await this.apiConnector({ data: { 'operation': 'logout' } })
  }

  async login (email, password) {
    var data = {
      'operation': 'login',
      'gdpr_email': email,
      'gdpr_password': password,
      'gdpr_instance_key': cookies.get('instance')
    }

    return await this.apiConnector({ data: data, ignoreDefaultParams: true })
  }

  isAuthenticated () {
    if (AppHelper.empty(cookies.get('accessKey')) && AppHelper.empty(cookies.get('crmid'))) {
      return false
    }

    return true
  }

  async fetchStructure () {
    return await this.apiConnector({ data: { 'operation': 'fetch_structure' } })
  }

  async fetchModules () {
    return await this.apiConnector({ data: { 'operation': 'fetch_modules' } })
  }

  async fetchList (module, parentRecord, filters, sort, page = null) {
    let data = {
      'operation': 'fetch_list',
      'module': module
    }

    if (parentRecord) {
      data['parent_record'] = parentRecord
    }

    if(filters) {
      data['filters'] = filters
    }

    if(sort) {
      data['sort'] = sort
    }

    if (page !== null) {
      data['page'] = page
    }

    return await this.apiConnector({data: data})
  }

  async fetchRecord (record) {
    return await this.apiConnector({ data: { 'operation': 'fetch_record', 'record': record } })
  }

  async fetchAvatar (record) {
    return await this.apiConnector({ data: { 'operation': 'fetch_avatar' , 'record' : record } });
  }

  async resetPassword (email) {
    let data = {
      'operation': 'reset_password',
      'gdpr_email': email,
      'gdpr_instance_key': cookies.get('instance')
    }

    return await this.apiConnector({ data: data, ignoreDefaultParams: true })
  }

  async requestAccess (email) {
    let data = {
      'operation': 'request_access',
      'gdpr_email': email,
      'gdpr_instance_key': cookies.get('instance')
    }

    return await this.apiConnector({ data: data, ignoreDefaultParams: true })
  }

  async update (fields, files) {
    let data = {
      'operation': 'update',
      'updated_fields': fields
    }

    if (files) {
      data = Object.assign(data, files)
    }

    return await this.apiConnector({ data: data })
  }

  async save (module, fields, parentRecord, files) {
    let data = {
      'operation': 'save',
      'fields': fields,
      'module': module
    }

    if (parentRecord) {
      data['parent_record'] = parentRecord
    }

    if (files) {
      data = Object.assign(data, files)
    }

    return await this.apiConnector({ data: data })
  }

  async export () {
    return await this.apiConnector({ data: { 'operation': 'export' } })
  }

  async forgetMe () {
    return await this.apiConnector({ data: { 'operation': 'forget_me' } })
  }

  async download (fileId, attachment) {
    let data = {
      'operation': 'download',
      'file_id': fileId
    }

    if(attachment){
      data['attachment'] = true
    }

    return await this.apiConnector({ data: data, responseType: 'blob' })
  }

  async invoiceDownload (record) {
    let data = {
      'operation': 'invoice_download',
      'record': record
    }

    return await this.apiConnector({ data: data, responseType: 'blob' })
  }

  async getLoginData() {
    let data = {
      'operation': 'fetch_login_page_data',
      'gdpr_instance_key': cookies.get('instance')
    }

    return await this.apiConnector({ data: data, ignoreDefaultParams: true })
  }

  async getInstanceKey(url) {
    let data = {
      'operation': 'get_instance_key_from_url',
      'client_url': url
    }

    return await this.apiConnector({ data: data, ignoreDefaultParams: true })
  }
}
